import React from 'react';
import { FaTags, FaUsers, FaStopwatch, FaTachometerAlt, FaDownload, FaShoppingCart } from 'react-icons/fa';
import { useTranslation } from '../../../shared/i18n';
import { GameHero, GameSection, FAQAccordion, Footer } from '../../../shared/components';
import logoSvg from '../../../assets/logo-ya/logo.svg';
import * as S from './LogoYaGamePage.styles';

const LogoYaGamePage = () => {
  const t = useTranslation();

  const heroBadges = [
    { label: t('logoYaGame.badges.physical'), variant: 'secondary' },
    { label: t('logoYaGame.badges.status'), variant: 'primary' },
  ];

  const heroActions = [
    {
      label: t('logoYaGame.actions.buy'),
      href: '/logo-ya/producto',
      variant: 'primary',
      icon: <FaShoppingCart />,
    },
    {
      label: t('logoYaGame.actions.instructions'),
      href: '#instrucciones',
      variant: 'outline',
    },
  ];

  const instructionsSteps = t('logoYaGame.instructions.steps', { returnObjects: true }) || [];
  const gameModes = t('logoYaGame.modes.items', { returnObjects: true }) || [];
  const faqItems = t('logoYaGame.faq.items', { returnObjects: true }) || [];

  return (
    <S.PageContainer>
      <GameHero
        title={t('logoYaGame.hero.title')}
        subtitle={t('logoYaGame.hero.subtitle')}
        badges={heroBadges}
        actions={heroActions}
        image={logoSvg}
        imageAlt={t('logoYaGame.hero.imageAlt')}
      />

      {/* Description & Overview */}
      <GameSection
        title={t('logoYaGame.overview.title')}
        subtitle={t('logoYaGame.overview.subtitle')}
      >
        <S.PlaceholderBanner>
          {t('common.placeholderNote')}
        </S.PlaceholderBanner>

        <S.GridThree>
          <S.InfoCard>
            <FaTags />
            <S.CardTitle>{t('logoYaGame.overview.cards.conceptTitle')}</S.CardTitle>
            <S.CardText>{t('logoYaGame.overview.cards.conceptText')}</S.CardText>
          </S.InfoCard>

          <S.InfoCard>
            <FaUsers />
            <S.CardTitle>{t('logoYaGame.overview.cards.playersTitle')}</S.CardTitle>
            <S.CardText>{t('logoYaGame.overview.cards.playersText')}</S.CardText>
          </S.InfoCard>

          <S.InfoCard>
            <FaStopwatch />
            <S.CardTitle>{t('logoYaGame.overview.cards.durationTitle')}</S.CardTitle>
            <S.CardText>{t('logoYaGame.overview.cards.durationText')}</S.CardText>
          </S.InfoCard>
        </S.GridThree>
      </GameSection>

      {/* Instructions */}
      <GameSection
        id="instrucciones"
        title={t('logoYaGame.instructions.title')}
        subtitle={t('logoYaGame.instructions.subtitle')}
        background="#ffffff"
      >
        <S.StepsContainer>
          {Array.isArray(instructionsSteps) && instructionsSteps.map((step, idx) => (
            <S.StepItem key={idx}>
              <S.StepNumber>{idx + 1}</S.StepNumber>

              <div>
                <S.CardTitle>{step.title}</S.CardTitle>
                <S.CardText>{step.description}</S.CardText>
              </div>
            </S.StepItem>
          ))}
        </S.StepsContainer>
      </GameSection>

      {/* Game Modes */}
      <GameSection
        title={t('logoYaGame.modes.title')}
        subtitle={t('logoYaGame.modes.subtitle')}
      >
        <S.GridThree>
          {Array.isArray(gameModes) && gameModes.map((mode, idx) => (
            <S.InfoCard key={idx}>
              <FaTachometerAlt />
              <S.CardTitle>{mode.title}</S.CardTitle>
              <S.CardText>{mode.description}</S.CardText>
            </S.InfoCard>
          ))}
        </S.GridThree>
      </GameSection>

      {/* Resources & Expansions */}
      <GameSection
        title={t('logoYaGame.expansions.title')}
        subtitle={t('logoYaGame.expansions.subtitle')}
        background="#ffffff"
      >
        <S.GridTwo>
          <S.InfoCard>
            <FaDownload />
            <S.CardTitle>{t('logoYaGame.expansions.downloadsTitle')}</S.CardTitle>
            <S.CardText>{t('logoYaGame.expansions.downloadsText')}</S.CardText>
          </S.InfoCard>

          <S.InfoCard>
            <FaTags />
            <S.CardTitle>{t('logoYaGame.expansions.futureTitle')}</S.CardTitle>
            <S.CardText>{t('logoYaGame.expansions.futureText')}</S.CardText>
          </S.InfoCard>
        </S.GridTwo>
      </GameSection>

      {/* FAQ */}
      <GameSection
        title={t('logoYaGame.faq.title')}
        subtitle={t('logoYaGame.faq.subtitle')}
      >
        <FAQAccordion items={Array.isArray(faqItems) ? faqItems : []} />
      </GameSection>

      {/* Commercial CTA */}
      <GameSection background="#ffffff">
        <S.CommercialCTA>
          <S.CTATitle>{t('logoYaGame.cta.title')}</S.CTATitle>
          <S.CTAText>{t('logoYaGame.cta.text')}</S.CTAText>
          <S.CTAButton href="/logo-ya/producto">
            {t('logoYaGame.cta.buttonText')}
          </S.CTAButton>
        </S.CommercialCTA>
      </GameSection>

      <Footer />
    </S.PageContainer>
  );
};

export default LogoYaGamePage;
